import { useEffect, useState, type FormEvent, type JSX } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import type {
  DocType,
  UesFieldDef,
} from "../../domain/entities/doc-type";
import { useCreateDocType } from "../hooks/use-create-doc-type";
import { useUpdateDocType } from "../hooks/use-update-doc-type";
import {
  isValid,
  validateDocType,
  wordCount,
  type DocTypeErrors,
} from "../schema-validation";
import { DeleteDocTypeDialog } from "./delete-doc-type-dialog";
import { SchemaEditor } from "./schema-editor";

export interface DocTypeEditorProps {
  docType: DocType | null;
  mode: "create" | "edit";
  onSaved: (saved: DocType) => void;
  onDeleted: (id: string) => void;
}

interface EditorState {
  key: string;
  displayName: string;
  description: string;
  uesFields: UesFieldDef[];
}

const EMPTY_STATE: EditorState = {
  key: "",
  displayName: "",
  description: "",
  uesFields: [],
};

function stateFromDocType(
  docType: DocType | null,
  mode: DocTypeEditorProps["mode"],
): EditorState {
  if (mode === "create" || !docType) {
    return EMPTY_STATE;
  }

  return {
    key: docType.key,
    displayName: docType.displayName,
    description: docType.description,
    uesFields: docType.uesFields,
  };
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

export function DocTypeEditor({
  docType,
  mode,
  onSaved,
  onDeleted,
}: DocTypeEditorProps): JSX.Element {
  const [state, setState] = useState<EditorState>(() =>
    stateFromDocType(docType, mode),
  );
  const [errors, setErrors] = useState<DocTypeErrors>({ fields: [] });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const createDocType = useCreateDocType();
  const updateDocType = useUpdateDocType(docType?.id ?? "");

  useEffect(() => {
    // Resetting controlled form state is required when the selected record changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState(stateFromDocType(docType, mode));
    setErrors({ fields: [] });
    setDeleteOpen(false);
  }, [docType, mode]);

  const setScalar = (
    field: "key" | "displayName" | "description",
    value: string,
  ): void => {
    setState((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleFieldsChange = (uesFields: UesFieldDef[]): void => {
    setState((current) => ({ ...current, uesFields }));
    setErrors((current) => ({ ...current, fields: [] }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const nextErrors = validateDocType(state, { isCreate: mode === "create" });
    setErrors(nextErrors);

    if (!isValid(nextErrors)) {
      return;
    }

    if (mode === "create") {
      createDocType.mutate(state, {
        onSuccess: onSaved,
        onError: (error) => {
          if (errorMessage(error).toLowerCase().includes("already exists")) {
            setErrors((current) => ({
              ...current,
              key: "A document type with this key already exists",
            }));
          }
        },
      });
      return;
    }

    if (!docType) {
      return;
    }

    updateDocType.mutate(
      {
        displayName: state.displayName,
        description: state.description,
        uesFields: state.uesFields,
      },
      { onSuccess: onSaved },
    );
  };

  const isSaving = createDocType.isPending || updateDocType.isPending;

  return (
    <>
      <form className="space-y-6" onSubmit={handleSubmit}>
        {mode === "edit" && docType && (
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">
              Schema version {docType.schemaVersion}
            </Badge>
            <Badge variant="secondary">{docType.source}</Badge>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="doc-type-key">Key</Label>
          <Input
            id="doc-type-key"
            value={state.key}
            readOnly={mode === "edit"}
            aria-invalid={Boolean(errors.key)}
            className={mode === "edit" ? "bg-muted text-muted-foreground" : undefined}
            onChange={(event) => setScalar("key", event.target.value)}
          />
          {mode === "edit" ? (
            <p className="text-xs text-muted-foreground">
              Key cannot be changed after creation.
            </p>
          ) : (
            errors.key && (
              <p className="text-xs text-destructive">{errors.key}</p>
            )
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="doc-type-display-name">Display name</Label>
          <Input
            id="doc-type-display-name"
            value={state.displayName}
            aria-invalid={Boolean(errors.displayName)}
            onChange={(event) =>
              setScalar("displayName", event.target.value)
            }
          />
          {errors.displayName && (
            <p className="text-xs text-destructive">{errors.displayName}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="doc-type-description">Description</Label>
            <span className="text-xs text-muted-foreground">
              {wordCount(state.description)}/100 words
            </span>
          </div>
          <Textarea
            id="doc-type-description"
            value={state.description}
            aria-invalid={Boolean(errors.description)}
            onChange={(event) =>
              setScalar("description", event.target.value)
            }
          />
          {errors.description && (
            <p className="text-xs text-destructive">{errors.description}</p>
          )}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Schema fields</h3>
          <SchemaEditor
            value={state.uesFields}
            onChange={handleFieldsChange}
            errors={errors.fields}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          {mode === "edit" && docType ? (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" disabled={isSaving}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>

      {mode === "edit" && docType && (
        <DeleteDocTypeDialog
          docType={docType}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={onDeleted}
        />
      )}
    </>
  );
}
